import { Spin } from "antd";
import i18n from "i18next";
import { type FC, useMemo, useRef } from "react";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router";

import {
    keepCardTypeReadOnlyFlagsUtils,
    stripDialogProfileCheckReadOnlyFields,
    updateWatchedFieldsUtils,
} from "src/app/features/ApplicationProfile/CardProfileUtils";
import { useCardProfileSaveMutation } from "src/app/features/ApplicationProfile/subFeatures/CardProfile/services/server/CardProfileApi";
import type { CardProfileQueryParams } from "src/app/features/ApplicationProfile/subFeatures/CardProfile/services/server/types";
import type { CardProfileType } from "src/app/features/ApplicationProfile/subFeatures/CardProfile/types/CardProfileType";
import { useCardTypeLoadQuery } from "src/app/features/CardType/services/server/CardTypeApi";
import { FormManager } from "../../../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../../../components/Form/hooks/useFormSubmitCompletion";
import { useLoadFormDefinitionQuery } from "../../../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../../../components/Form/types";
import type { FormProps } from "../../../../../../../components/Form/types/FormProps";
import { InheritedFieldChangeConfirmationModal } from "../../../../../../../components/Modal/InheritedFieldChangeConfirmationModal";
import { ResourceName, ResourceVariant } from "../../../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../../../core/router/types/routerTypes";
import type { ID } from "../../../../../../../core/types/fe";
import { isTheSameByDeepCompare } from "../../../../../../../core/utils/utilFunctions";
import type { CardProfileEntity } from "../../types/CardProfileEntity";
import { CardProfileFormConfigMap } from "./CardProfileFormConfig";

export const CardProfileCreateForm: FC<FormProps> = ({ runSubmit, formAction }) => {
    const { appProfileId, id } = useParams();
    const [searchParams] = useSearchParams();

    const type = searchParams.get("type") as CardProfileType;
    const location = useLocation();
    const isApac = location.pathname.includes("/apac");

    const resourceUrl = `${ResourceName.APPLICATION_PROFILE_LISTS}/${appProfileId}/${ResourceName.APPLICATION_PROFILE_CARD_DEFINITION}`;
    const resourcePath = isApac
        ? `${ResourceName.APPLICATION_PROFILE_LISTS}/${ResourceVariant.APAC}/${appProfileId}/${ResourceName.APPLICATION_PROFILE_CARD_DEFINITION_APAC}`
        : `${ResourceName.APPLICATION_PROFILE_LISTS}/${appProfileId}/${ResourceName.APPLICATION_PROFILE_CARD_DEFINITION_EFT4F}`;
    const parentEditPath = isApac
        ? `${BASE_PATH}/${ResourceName.APPLICATION_PROFILE_LISTS}/${ResourceVariant.APAC}/${FORM_ACTION.EDIT}/${appProfileId}`
        : `${BASE_PATH}/${ResourceName.APPLICATION_PROFILE_LISTS}/${FORM_ACTION.EDIT}/${appProfileId}`;
    const completeSubmit = useFormSubmitCompletion(formAction, parentEditPath);

    const formNameConfigs = isApac ? CardProfileFormConfigMap.forms.apac : CardProfileFormConfigMap.forms.eft4f;
    const formDefinitionString = formNameConfigs[type];

    // set save params
    useEffect(() => {
        setQueryConfig({ appProfileId });
    }, [appProfileId, resourceUrl]);

    // load form definition
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery(formDefinitionString as string);

    const BASIC_PARAMS = 0;
    const cardTypeIdConfig = useMemo(
        () => formDefinition?.formConfiguration?.tabs[BASIC_PARAMS].fields?.find((item) => item.name === "cardTypeId"),
        [formDefinition],
    );
    const updateFields = cardTypeIdConfig?.updateFields;

    // manage form data
    const [formData, setFormData] = useState<CardProfileEntity>({} as CardProfileEntity);
    const prevProfileIdRef = useRef<ID | undefined>(undefined);
    const prevDialogProfileIdRef = useRef<ID | undefined>(undefined);
    const pendingFormDataRef = useRef<CardProfileEntity | null>(null);
    const formDataRef = useRef(formData);
    const [cardTypeId, setCardTypeId] = useState<ID | undefined>(undefined);
    const [dialogProfileId, setDialogProfileId] = useState<ID | undefined>(undefined);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

    // manage submit action
    const [saveFormData, { isLoading: saveFormIsInProgress }] = useCardProfileSaveMutation();
    const [queryConfig, setQueryConfig] = useState<CardProfileQueryParams>({});

    const applyUpdatedData = useCallback((updatedData: CardProfileEntity) => {
        formDataRef.current = updatedData;

        if (updatedData.cardTypeId !== prevProfileIdRef.current) {
            setCardTypeId(updatedData.cardTypeId);
            setFormData(updatedData);

            if (updatedData.cardTypeId === undefined) {
                prevProfileIdRef.current = undefined;
            }
        }

        if (updatedData.dialogProfileId !== prevDialogProfileIdRef.current) {
            setDialogProfileId(updatedData.dialogProfileId);
        }
    }, []);

    const onChangeCallback = useCallback(
        (newData: CardProfileEntity) => {
            const updatedData = keepCardTypeReadOnlyFlagsUtils(newData, formDataRef.current, updateFields);

            const hasCardTypeChange = updatedData.cardTypeId !== prevProfileIdRef.current;
            if (hasCardTypeChange && updatedData.cardTypeId !== undefined) {
                pendingFormDataRef.current = updatedData;
                setIsConfirmModalOpen(true);
                setFormData({ ...formDataRef.current });
                return;
            }

            applyUpdatedData(updatedData);
        },
        [applyUpdatedData, updateFields],
    );

    const onConfirmCardTypeChange = useCallback(() => {
        const pendingData = pendingFormDataRef.current;
        pendingFormDataRef.current = null;
        setIsConfirmModalOpen(false);
        if (pendingData) {
            applyUpdatedData(pendingData);
        }
    }, [applyUpdatedData]);

    const onCancelCardTypeChange = useCallback(() => {
        pendingFormDataRef.current = null;
        setIsConfirmModalOpen(false);
        setFormData({ ...formDataRef.current });
    }, []);

    // update watchedFields values
    const { data: cardTypeEntity } = useCardTypeLoadQuery({ entityId: `${cardTypeId}` }, { skip: !cardTypeId });

    useEffect(() => {
        // Ensure dialogVehicleIdFormat is always set to 'UNDEFINED' if not set
        if (!formData.dialogVehicleIdFormat) {
            const updatedFormData = {
                ...formDataRef.current,
                dialogVehicleIdFormat: "UNDEFINED",
            } as CardProfileEntity;

            setFormData(updatedFormData);
            formDataRef.current = updatedFormData;
        }
    }, [formData.dialogVehicleIdFormat]);

    useEffect(() => {
        const currentDialogProfileId = dialogProfileId;
        const prevDialogProfileId = prevDialogProfileIdRef.current;
        if (currentDialogProfileId !== prevDialogProfileId) {
            prevDialogProfileIdRef.current = currentDialogProfileId;
            const isEmpty = currentDialogProfileId === null || currentDialogProfileId === undefined;
            const updatedFormData = isEmpty
                ? {
                      ...formDataRef.current,
                      dialogsYesCheckReadOnly: false,
                      dialogsNoCheckReadOnly: false,
                      dialogsMandatoryCheckReadOnly: false,
                      dialogsCheckCheckReadOnly: false,
                      dialogVehicleIdFormatCheckReadOnly: false,
                  }
                : {
                      ...formDataRef.current,
                      dialogsYes: undefined,
                      dialogsNo: undefined,
                      dialogsMandatory: undefined,
                      dialogsCheck: undefined,
                      dialogVehicleIdFormat: "UNDEFINED",
                      dialogsYesCheckReadOnly: true,
                      dialogsNoCheckReadOnly: true,
                      dialogsMandatoryCheckReadOnly: true,
                      dialogsCheckCheckReadOnly: true,
                      dialogVehicleIdFormatCheckReadOnly: true,
                  };
            if (!isTheSameByDeepCompare(formDataRef.current, updatedFormData as CardProfileEntity)) {
                setFormData(updatedFormData as CardProfileEntity);
                formDataRef.current = updatedFormData as CardProfileEntity;
            }
        }
    }, [dialogProfileId]);

    const updateWatchedFields = useCallback(() => {
        const currentProfileId = formDataRef.current?.cardTypeId;

        if (!currentProfileId || !cardTypeEntity || !updateFields || String(cardTypeEntity.id) !== String(currentProfileId)) return null;

        prevProfileIdRef.current = currentProfileId;

        if (!formDataRef.current) return null;

        return updateWatchedFieldsUtils(cardTypeEntity, formDataRef.current, updateFields);
    }, [cardTypeEntity, updateFields]);

    useEffect(() => {
        if (!cardTypeEntity || !cardTypeId || String(cardTypeEntity.id) !== String(cardTypeId)) {
            return;
        }

        const prevProfileId = prevProfileIdRef.current;

        if (cardTypeId !== prevProfileId) {
            let newFormData = updateWatchedFields();
            if (newFormData && !isTheSameByDeepCompare(formDataRef.current, newFormData)) {
                setFormData(newFormData);
                formDataRef.current = newFormData;
                newFormData = null;
            }
        }
    }, [cardTypeEntity, cardTypeId, updateWatchedFields]);

    const onValidatedCallback = useCallback(
        async (valid: boolean, validatedValues?: CardProfileEntity) => {
            const currentFormData = validatedValues ? { ...validatedValues } : formDataRef.current;
            if (!valid || !currentFormData) {
                return;
            }

            formDataRef.current = currentFormData;
            const sanitizedFormData = stripDialogProfileCheckReadOnlyFields(currentFormData);
            const result = await saveFormData({ cardProfilesFormData: sanitizedFormData, cardProfileQuery: queryConfig });

            const hasError = "error" in result;
            if (!hasError) {
                completeSubmit(
                    i18n.t("common.formSaveSuccess", {
                        id: result.data.id,
                        resource: "Card Profile",
                    }),
                    `${BASE_PATH}/${resourcePath}/${FORM_ACTION.EDIT}/${result.data.id}?type=${type}`,
                );
            }
        },
        [saveFormData, queryConfig, completeSubmit, resourcePath, type],
    );

    // Memoize formData prop so its reference only changes when content changes.
    // Prevents FormManager from resetting Ant Form fields when parent re-renders.
    const formManagerData = useMemo(() => (type ? { ...formData, id, type } : { ...formData, id }), [formData, id, type]);

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching}>
            {formDefinition?.formConfiguration && (
                <FormManager<CardProfileEntity>
                    name={formDefinition.formConfiguration.resource}
                    formData={formManagerData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
            <InheritedFieldChangeConfirmationModal
                open={isConfirmModalOpen}
                onConfirm={onConfirmCardTypeChange}
                onCancel={onCancelCardTypeChange}
            />
        </Spin>
    );
};
